/**
 * gc-read-file — turn whatever the coach handed us into a typed GameChanger source.
 *
 * Browser-only (pdfjs). Content decides the type, never the filename: GameChanger
 * ships the scorebook and the game stats PDF under the same name, and a CSV can
 * arrive as a paste, from the share sheet, or from Files with a generic MIME type.
 */

import { loadPdfPages } from '../pdf/pdfText';
import { detectGcPdfType } from '../pdf/detectGcPdfType';
import { parseScorebook } from '../pdf/scorebookParser';
import { parseGameStatsSheet } from '../pdf/gameStatsPdfParser';
import type { ParsedGCPlayer } from '../gcCsvParser';
import type { ParsedScorebook, ParsedGameStatsSheet } from '../../types/import.types';
import { parseCsvText, looksLikeGcCsv, csvGameCount, csvPlayedRows } from './gc-sources';

export type ReadResult =
  | { kind: 'scorebook'; fileName: string; book: ParsedScorebook }
  | { kind: 'boxScore'; fileName: string; sheet: ParsedGameStatsSheet }
  | { kind: 'csv'; fileName: string; rows: ParsedGCPlayer[]; via: 'file' | 'paste' }
  | { kind: 'rejected'; fileName: string; message: string };

const GC_HOW_TO_CSV = 'In GameChanger: Stats, pick this one game, then Export.';

/** Validate parsed CSV rows: must be a GameChanger export, and exactly one game. */
export function readCsvRows(rows: ParsedGCPlayer[], fileName: string, via: 'file' | 'paste'): ReadResult {
  if (!rows.length) {
    return { kind: 'rejected', fileName, message: `That doesn't look like a GameChanger stats export. ${GC_HOW_TO_CSV}` };
  }
  const games = csvGameCount(rows);
  if (games > 1) {
    // A season export would write season totals as one game's box score.
    return { kind: 'rejected', fileName, message: `That CSV covers ${games} games (season totals). Dugout needs the one-game export. ${GC_HOW_TO_CSV}` };
  }
  if (!csvPlayedRows(rows)) {
    return { kind: 'rejected', fileName, message: `That CSV has no stats in it yet. ${GC_HOW_TO_CSV}` };
  }
  return { kind: 'csv', fileName, rows, via };
}

export function readPastedCsv(text: string): ReadResult {
  return readCsvRows(parseCsvText(text), 'Pasted stats', 'paste');
}

export async function readGcFile(file: File): Promise<ReadResult> {
  const fileName = file.name || 'Shared file';
  try {
    const isPdfName = /\.pdf$/i.test(fileName) || file.type === 'application/pdf';
    if (!isPdfName) {
      const text = await file.text();
      if (/\.csv$/i.test(fileName) || /csv|text\/plain/.test(file.type) || looksLikeGcCsv(text)) {
        return readCsvRows(parseCsvText(text), fileName, 'file');
      }
    }

    const pages = await loadPdfPages(file);
    const detected = detectGcPdfType(pages);
    if (detected.type === 'scorebook') {
      const book = parseScorebook(pages);
      if (!book.pages.some(p => p.atBats.length)) {
        return { kind: 'rejected', fileName, message: 'That scorebook PDF came through, but its grid could not be read. Try exporting it from GameChanger again.' };
      }
      return { kind: 'scorebook', fileName, book };
    }
    if (detected.type === 'gameStatsSheet') {
      const sheet = parseGameStatsSheet(pages);
      if (!sheet.teams.some(t => t.batters.length)) {
        return { kind: 'rejected', fileName, message: 'That game stats PDF came through, but its tables could not be read. Try exporting it from GameChanger again.' };
      }
      return { kind: 'boxScore', fileName, sheet };
    }
    return { kind: 'rejected', fileName, message: "That file isn't a GameChanger scorebook, game stats PDF or stats CSV." };
  } catch {
    return { kind: 'rejected', fileName, message: "That file couldn't be opened. If it's from GameChanger, try sharing it again." };
  }
}
